import React, { useEffect, useState } from 'react';
import SearchableMultiSelect from './SearchableMultiSelect';
import api from '../../utils/api';

// Meta insights ACTION TYPES as report metrics — everything in the `actions` /
// `conversions` pool that is neither a pixel event nor a custom conversion: link clicks,
// landing page views, page/post engagement, messaging, app events, the omni_* family.
// The third of the three Meta selection pickers, and structurally the twin of
// MetaPixelEvents / MetaCustomConversions.
//
// Discovery reads the same insights call the pixel-event picker does (server-side
// discoverAccountActionPool) and keeps exactly what that one discards — see
// classifyReportableActions in server/services/cognitive-flows/metaAdsReportService.js.
// Each selection is resolved per row by appendActionColumns, the same helper the other
// two pickers use; they differ only in how they derive an action_type.
//
// Shared by the three Meta editors that must stay in sync: components/nodes/AdsReadConfig.js,
// pages/CognitiveFlowsDataSourcesPage.jsx and components/dashboard/AgentSettingsModal.js.

// The two status reports emit fixed columns and ignore metric selection.
const FIXED_COLUMN_REPORTS = new Set(['ad_enhancements', 'adset_advantage']);

/** True when actions apply to this report (Meta insights reports only). */
export function supportsActions(platform, reportType) {
  return platform === 'meta' && !!reportType && !FIXED_COLUMN_REPORTS.has(reportType);
}

/**
 * The account's reportable action types, refetched whenever the account changes.
 * Not scoped by pixel — an action type carries no pixel, so the list is account-wide.
 */
export function useMetaActions(accountId, { enabled = true } = {}) {
  const [list, setList] = useState([]);
  const [warning, setWarning] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled || !accountId) { setList([]); setWarning(null); setError(null); return undefined; }
    let cancelled = false;
    setLoading(true);
    api.get('/meta-ads/actions', { params: { accountId } })
      .then(({ data }) => {
        if (cancelled) return;
        // A `warning` means discovery failed outright. Unlike pixel events there is no
        // curated fallback to degrade to, so the list is empty — but this is still not
        // the `error` path: the request itself succeeded, and any already-saved
        // selections must stay visible so they can be kept or cleared.
        setList(data?.actions || []);
        setWarning(data?.warning || null);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setList([]);
        setWarning(null);
        setError(err?.response?.data?.error || 'Could not load actions');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [accountId, enabled]);

  return { actions: list, warning, loading, error };
}

/**
 * Searchable multi-select of the account's reportable action types.
 * `selected` / `onChange` carry [{ id, name, actionType }] — the name is persisted so a
 * saved source keeps a stable column header, and `actionType` is Meta's own string,
 * carried through so the server never has to reconstruct one.
 */
export default function ActionsSelector({
  accountId,
  selected = [],
  onChange,
  disabled = false,
  styles,
  includeCost = false,
  onIncludeCostChange,
  includeValue = false,
  onIncludeValueChange,
}) {
  const { actions, warning, loading, error } = useMetaActions(accountId);

  // The cost toggle lives inside this component rather than in each host, so every Meta
  // editor picks it up without growing its own copy — same rationale as the twins.
  // Cost and value are siblings: each adds one companion column per selection.
  const toggle = (checked, onToggle, text) => (
    <label style={{
      display: 'inline-flex', alignItems: 'center', gap: '7px',
      fontSize: '12px', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.7 : 1,
    }}>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onToggle(e.target.checked)}
        disabled={disabled}
        style={{ accentColor: 'var(--primary)', flexShrink: 0 }}
      />
      <span>{text}</span>
    </label>
  );
  const costToggle = onIncludeCostChange ? toggle(includeCost, onIncludeCostChange, 'Include cost per action') : null;
  const valueToggle = onIncludeValueChange ? toggle(includeValue, onIncludeValueChange, 'Include action value') : null;
  const toggleRow = (costToggle || valueToggle) ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap', marginTop: '6px' }}>
      {costToggle}
      {valueToggle}
    </div>
  ) : null;

  if (!accountId) return <span style={styles.hint}>Select an ad account to load its actions.</span>;
  if (loading) return <span style={styles.hint}>Loading actions…</span>;
  if (error) return <span style={styles.hint}>{error}</span>;

  if (!actions.length && !selected.length && !warning) {
    return <span style={styles.hint}>This ad account reported no actions in the last 90 days.</span>;
  }

  const known = new Set(actions.map((a) => String(a.id)));
  const stranded = selected.filter((a) => !known.has(String(a.id)));

  const options = [
    ...actions.map((a) => ({
      value: String(a.id),
      label: a.name,
      // The raw action_type is the hint rather than the label: it is what Meta actually
      // reports, so it disambiguates two friendly names that read alike.
      hint: a.actionType && a.actionType !== a.name ? a.actionType : undefined,
    })),
    // A saved selection missing from the list means one of two very different things,
    // and conflating them costs people their columns: when discovery FAILED we know
    // nothing about this action, so it must not be labelled as removable.
    ...stranded.map((a) => (warning
      ? {
        value: String(a.id),
        label: a.name || a.id,
        hint: "Kept from your saved selection — this account's actions could not be read just now.",
      }
      : {
        value: String(a.id),
        label: `${a.name || a.id} (not reported by this account)`,
        hint: 'No delivery in the last 90 days — untick to remove the column.',
      })),
  ];

  const metaFor = (id) =>
    actions.find((a) => String(a.id) === id)
    // Prefer the saved selection over re-deriving: it already carries the actionType
    // Meta gave us, and guessing one would mint a permanently-zero column.
    ?? selected.find((a) => String(a.id) === id)
    ?? { id, name: id, actionType: id };

  return (
    <>
      {warning && (
        <span style={{ ...styles.hint, color: '#b45309', display: 'block', marginBottom: '6px' }}>
          {warning}
        </span>
      )}
      <SearchableMultiSelect
        value={selected.map((a) => String(a.id))}
        options={options}
        onChange={(ids) => onChange(ids.map((id) => {
          const m = metaFor(id);
          return { id, name: m.name || id, actionType: m.actionType || id };
        }))}
        styles={styles}
        disabled={disabled}
        placeholder="— Select actions —"
        emptyText="No matching actions"
        summarize={(n) => `${n} actions selected`}
      />
      {toggleRow}
    </>
  );
}
